if (!customElements.get('collection-swiper')) {
  customElements.define(
    'collection-swiper',
    class CollectionSwiper extends HTMLElement {
      connectedCallback() {
        // Init is driven by home-collection-tabs after layout is ready.
      }

      disconnectedCallback() {
        this.destroySwiper();
      }

      initSwiper() {
        if (this.swiper) return;
        if (this.closest('.collection-panel')?.hasAttribute('hidden')) return;

        const scrollbarEl = this.parentElement?.querySelector('.coll-scroll');

        this.swiper = new Swiper(this, {
          loop: false,
          slidesPerView: 5,
          spaceBetween: 20,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          scrollbar: scrollbarEl
            ? {
                el: scrollbarEl,
                draggable: true,
                snapOnRelease: true,
                dragSize: 30,
              }
            : undefined,
          breakpoints: {
            1280: {
              slidesPerView: 4,
              spaceBetween: 15,
            },
            999: {
              slidesPerView: 3,
              spaceBetween: 10,
            },
            768: {
              slidesPerView: 2,
              spaceBetween: 10,
            },
            590: {
              slidesPerView: 2,
              spaceBetween: 15,
            },
          },
          on: {
            init: () => this.notifyNav(),
            resize: () => this.notifyNav(),
            update: () => this.notifyNav(),
          },
        });
      }

      destroySwiper() {
        if (!this.swiper) return;
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      updateLayout() {
        if (!this.swiper) {
          this.initSwiper();
          this.notifyNav();
          return;
        }

        this.swiper.update();
        this.swiper.slideTo(0, 0);

        if (this.swiper.scrollbar && typeof this.swiper.scrollbar.updateSize === 'function') {
          this.swiper.scrollbar.updateSize();
        }

        this.notifyNav();
      }

      needsNavigation() {
        if (!this.swiper) return false;
        if (this.swiper.isLocked) return false;
        return !(this.swiper.isBeginning && this.swiper.isEnd);
      }

      notifyNav() {
        this.closest('home-collection-tabs')?.syncNav();
      }
    }
  );
}

if (!customElements.get('home-collection-tabs')) {
  customElements.define(
    'home-collection-tabs',
    class HomeCollectionTabs extends HTMLElement {
      connectedCallback() {
        this.pills = Array.from(this.querySelectorAll('[data-tab]'));
        this.panels = Array.from(this.querySelectorAll('.collection-panel'));
        this.prevBtn = this.querySelector('.prev-item');
        this.nextBtn = this.querySelector('.next-item');

        this.onPillClick = (event) => this.select(event.currentTarget.dataset.tab);
        this.onPrev = () => this.activeSwiper?.swiper?.slidePrev();
        this.onNext = () => this.activeSwiper?.swiper?.slideNext();
        this.onResize = () => {
          this.activeSwiper?.updateLayout();
          this.syncNav();
        };

        this.pills.forEach((pill) => pill.addEventListener('click', this.onPillClick));
        this.prevBtn?.addEventListener('click', this.onPrev);
        this.nextBtn?.addEventListener('click', this.onNext);
        window.addEventListener('resize', this.onResize);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.refreshActive());
        });
      }

      disconnectedCallback() {
        this.pills?.forEach((pill) => pill.removeEventListener('click', this.onPillClick));
        this.prevBtn?.removeEventListener('click', this.onPrev);
        this.nextBtn?.removeEventListener('click', this.onNext);
        window.removeEventListener('resize', this.onResize);
      }

      get activeSwiper() {
        return this.activePanel?.querySelector('collection-swiper');
      }

      get activePanel() {
        return this.panels.find((panel) => !panel.hasAttribute('hidden'));
      }

      refreshActive() {
        this.activeSwiper?.updateLayout();
        this.syncNav();
      }

      syncNav() {
        const show = Boolean(this.activeSwiper?.needsNavigation());

        this.prevBtn?.classList.toggle('is-hidden', !show);
        this.nextBtn?.classList.toggle('is-hidden', !show);

        this.panels.forEach((panel) => {
          const scrollbar = panel.querySelector('.coll-scroll');
          const panelActive = !panel.hasAttribute('hidden');
          scrollbar?.classList.toggle('is-hidden', !(panelActive && show));
        });
      }

      select(tabId) {
        if (!tabId) return;

        this.pills.forEach((pill) => {
          const active = pill.dataset.tab === tabId;
          pill.classList.toggle('is-active', active);
          pill.setAttribute('aria-selected', String(active));
        });

        this.panels.forEach((panel) => {
          if (panel.dataset.panel === tabId) {
            panel.removeAttribute('hidden');
          } else {
            panel.setAttribute('hidden', '');
          }
        });

        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.refreshActive());
        });
      }
    }
  );
}
